import React from 'react';
import { Box, Typography, Grid, useTheme, useMediaQuery } from '@mui/material';
import WhyChooseUsCard from '../../../components/common/WhyChooseUsCard';
import { useDispatch, useSelector } from 'react-redux';
import { fetchCmsHomeAction } from '../../../store/slices/cmsSlice';

const WhyChooseUs = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm')); // <600px
  const isMediumScreen = useMediaQuery('(min-width: 600px) and (max-width: 875px)'); // 600-835px with scroll
  const isTablet = useMediaQuery('(min-width: 875px) and (max-width: 950px)'); // 835-950px with grid
  const isDesktop = useMediaQuery('(min-width: 950px)'); // >950px

  const dispatch = useDispatch();
  const cmsHome = useSelector((state) => state.cms.home);

  React.useEffect(() => {
    if (!cmsHome) dispatch(fetchCmsHomeAction());
  }, [cmsHome, dispatch]);

  const { sectionTitle, sectionDescription, cardItems } = React.useMemo(() => {
    const data = Array.isArray(cmsHome?.data) ? cmsHome.data : [];
    const sec = data.find((s) => s?.id === 84);
    const rel = Array.isArray(sec?.relatedCMS) ? sec.relatedCMS : [];
    const iconOrder = ['WCUclock','WCUheart','WCUcalendar','WCUmoney','WCUhappyemoji','WCUverify'];
    const cards = rel.slice(0, 6).map((r, idx) => ({
      id: idx + 1,
      icon: iconOrder[idx] || iconOrder[0],
      title: r?.title || '',
      description: r?.description || '',
    }));
    return {
      sectionTitle: (sec?.title || '').trim(),
      sectionDescription: (sec?.description || '').trim(),
      cardItems: cards,
    };
  }, [cmsHome]);

  // Responsive styles function
  const getResponsiveStyles = () => {
    if (isMobile) {
      return {
        container: {
          width: '100%',
          paddingLeft: '16px',
          paddingRight: '16px'
        },
        title: {
          fontSize: '18px',
          lineHeight: '26px',
          letterSpacing: '-2%',
          marginBottom: '10px'
        },
        description: {
          fontSize: '10px',
          lineHeight: '19.42px',
          letterSpacing: '-2%',
          marginBottom: '20px'
        },
        sectionHeader: {
          fontSize: '14px',
          lineHeight: '100%',
          letterSpacing: '2px',
          marginBottom: '10px'
        }
      };
    } else if (isMediumScreen) {
      return {
        container: {
          width: '94.44%',
          paddingLeft: '20px',
          paddingRight: '20px'
        },
        title: {
          fontSize: '26px',
          marginBottom: '18px'
        },
        description: {
          fontSize: '14px',
          lineHeight: '21px',
          marginBottom: '28px'
        },
        sectionHeader: {
          fontSize: '13px',
          marginBottom: '22px'
        }
      };
    } else if (isTablet) {
      return {
        container: {
          width: '94.44%',
          paddingLeft: '24px',
          paddingRight: '24px'
        },
        title: {
          fontSize: '30px',
          marginBottom: '18px'
        },
        description: {
          fontSize: '15px',
          lineHeight: '22px',
          marginBottom: '32px'
        },
        sectionHeader: {
          fontSize: '13px',
          marginBottom: '22px'
        }
      };
    } else {
      return {
        container: {
          width: '94.44%',
          paddingLeft: '0px',
          paddingRight: '0px'
        },
        title: {
          fontSize: '36px',
          marginBottom: '20px'
        },
        description: {
          fontSize: '16px',
          lineHeight: '24px',
          marginBottom: '40px'
        },
        sectionHeader: {
          fontSize: '14px',
          marginBottom: '24px'
        }
      };
    }
  };

  const styles = getResponsiveStyles();

  return (
    <Box
      sx={{
        ...styles.container,
        maxWidth: '1440px',
        margin: '0 auto',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        marginBottom: isMobile ? '0px' : '60px',
      }}
    >
      {/* Green Dot and Title */}
      <Box sx={{ 
        display: 'flex', 
        alignItems: 'center', 
        gap: isMobile ? '8px' : '10px', 
        alignSelf: 'flex-start', 
        marginBottom: styles.sectionHeader.marginBottom
      }}>
        <Box
          sx={{
            width: isMobile ? '6px' : '8px',
            height: isMobile ? '6px' : '8px',
            borderRadius: '50%',
            backgroundColor: '#A9C74E',
          }}
        />
        <Typography
          sx={{
            fontFamily: 'Arial',
            fontWeight: 400,
            fontSize: styles.sectionHeader.fontSize,
            lineHeight: styles.sectionHeader.lineHeight,
            letterSpacing: styles.sectionHeader.letterSpacing,
            verticalAlign: 'middle',
            textTransform: 'uppercase',
            color: '#131313',
          }}
        >
          WHY CHOOSE US
        </Typography>
      </Box>

      {/* Title */}
      <Typography
        sx={{
          fontWeight: 700,
          fontSize: styles.title.fontSize,
          lineHeight: styles.title.lineHeight,
          letterSpacing: styles.title.letterSpacing,
          verticalAlign: 'middle',
          textTransform: 'capitalize',
          color: '#1D1D1B',
          alignSelf: 'flex-start',
          marginBottom: styles.title.marginBottom,
          maxWidth: isMobile ? '100%' : '642px',
          textAlign: 'left'
        }}
      >
        {sectionTitle}
      </Typography>

      {/* Description */}
      <Typography
        sx={{
          fontWeight: 400,
          fontSize: styles.description.fontSize,
          lineHeight: styles.description.lineHeight,
          letterSpacing: styles.description.letterSpacing,
          verticalAlign: 'middle',
          color: '#7D7D7D',
          maxWidth: isMobile ? '100%' : '1440px',
          textAlign: 'left',
          alignSelf: 'flex-start',
          marginBottom: styles.description.marginBottom
        }}
      >
        {sectionDescription}
      </Typography>

      {/* Why Choose Us Cards - Mobile/Medium Slider or Desktop/Tablet Grid */}
      {(isMobile || isMediumScreen) ? (
        // Mobile & Medium Screen Slider (600-835px with scroll)
        <Box
          sx={{
            display: 'flex',
            width: '100%',
            gap: isMobile ? '16px' : '18px',
            overflowX: 'auto',
            overflowY: 'hidden',
            paddingBottom: '10px',
            scrollbarWidth: 'none',
            msOverflowStyle: 'none',
            '&::-webkit-scrollbar': {
              display: 'none'
            }
          }}
        >
          {cardItems.map((card) => (
            <Box key={card.id} sx={{ flexShrink: 0 }}>
              <WhyChooseUsCard 
                {...card} 
                isMobile={isMobile} 
                isMediumScreen={isMediumScreen}
              />
            </Box>
          ))}
        </Box>
      ) : (
        // Desktop/Tablet Grid (835px+ with grid)
        <Grid 
          container 
          spacing={isTablet ? "16px" : "20px"} 
          justifyContent="center"
          sx={{ 
            width: '100%',
            '@media (min-width:1440px)': { justifyContent: 'flex-start' } // Flex-start at >=1440px, center below
          }}
        >
          {cardItems.map((card) => (
            <Grid item key={card.id}>
              <WhyChooseUsCard 
                {...card} 
                isMobile={isMobile} 
                isTablet={isTablet}
                isDesktop={isDesktop}
              />
            </Grid>
          ))}
        </Grid>
      )}
    </Box>
  );
};

export default WhyChooseUs;
